import React, { useState } from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { Form, Formik } from 'formik';
import { Paper } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import CommonButton from '../../../common/CommonButton';
import DialogContent from '@material-ui/core/DialogContent';
import Dialog from '@material-ui/core/Dialog';
import {
  fetchAdministrationData,
  updateAdministrationItem,
} from '../../../../actions/administration/administration-actions';
import CommonPagination from '../../../common/CommonPagination';
import AdministrationItemsFormFields, { sliceRows } from '../AdministrationItemsFormFields';
import { ADMINISTRATION_BOOKKEEPING_CATEGORIES } from '../AdministrationPagesConfig';
import classNames from 'classnames';
import { fieldCustomStyles, sectionWrapperStyles } from '../AddNewItemDialog';
import {
  ALMOST_WHITE_COLOR,
  BACKGROUND_DARK,
  BACKGROUND_WHITE, DARK_CUSTOM,
  DODGER_BLUE, WHITE_COLOR,
} from '../../../../const/color-const';
import PropTypes from 'prop-types';

const useStyles = makeStyles(theme => ({
  formContent: {
    padding: theme.spacing(0),
    transition: 'max-height 1s ease-in-out',
    overflow: 'hidden',
    background: theme.palette.type === 'light' ? WHITE_COLOR : DARK_CUSTOM,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2, 0, 1, 0),
      maxHeight: '100%',
      overflow: 'visible',
    },
    '& div[class*="sectionWrapper"]': {
      '& > div:last-of-type': {
        display: 'flex',
        flexDirection: 'column-reverse',
        position: 'relative',
        '& > div.accountNumberWrapper:not(:only-child)': {
          position: 'static',
          [theme.breakpoints.up('md')]: {
            position: 'absolute',
            bottom: -56,
          },
        },
      },
    },
  },
  saveButton: {
    marginLeft: theme.spacing(2),
  },
  paperWrapper: {
    margin: theme.spacing(2),
  },
  expanded: {
    background: 'transparent',
    maxHeight: 5000,
    overflow: 'visible',
  },
  ...sectionWrapperStyles(theme),
  '& [class*="MuiTextField"]': {
    ...fieldCustomStyles(theme),
    boxShadow: 'none',
  },
  accordionArrow: {
    background: theme.palette.type === 'light' ? BACKGROUND_WHITE : BACKGROUND_DARK,
    border: theme.palette.type === 'light' ? `1px solid ${ALMOST_WHITE_COLOR}` : `none`,
    transition: 'all 0.4s ease',
    position: 'absolute',
    cursor: 'pointer',
    borderRadius: 20,
    color: DODGER_BLUE,
    zIndex: 100,
    width: 26,
    height: 26,
    right: 15,
    top: 40,
  },
  accordionOpened: {
    transform: 'rotate(180deg)',
  },
  dialogContent: {
    background: theme.palette.type === 'light' ? BACKGROUND_WHITE : BACKGROUND_DARK,
    minWidth: '50%',
    width: '100%',
    '& div[class*="paginationWrapper"]': {
      marginTop: theme.spacing(2),
    },
  },
  selectedMachineName: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
  },
}));

const AdministrationBookkeepingPopup = ({
                                          machineId,
                                          selectedMachineLabel,
                                          vatOptions,
                                          activePopup,
                                          setPopupActive,
                                          administrationData,
                                          fetchAdministrationData,
                                          updateAdministrationItem,
                                        }) => {
  const [accordion, setAccordion] = useState([]);
  const [page, setPage] = useState(1);
  const classes = useStyles();
  
  const getPagination = () => {
    return <CommonPagination
      page={page}
      itemsFrom={administrationData.itemsFrom}
      itemsTo={administrationData.itemsTo}
      pageCount={administrationData.pageCount}
      onChange={(event, value) => {
        setPage(value);
        return fetchAdministrationData('Category', machineId, { text: null }, value - 1);
      }}
      totalCount={administrationData.totalCount}
    />;
  };
  
  return (
    <Dialog maxWidth={'lg'} open={activePopup} onClose={() => setPopupActive(false)}>
      <DialogContent className={classes.dialogContent}>
        <p className={classes.selectedMachineName}>{selectedMachineLabel}</p>
        
        {administrationData?.items.length > 0 &&
        (<>
            {getPagination()}
            {administrationData.items.map((item, index) => {
              const rows = sliceRows(ADMINISTRATION_BOOKKEEPING_CATEGORIES.fields);
              return <Paper className={classes.paperWrapper} key={index} elevation={2}>
                <Formik
                  enableReinitialize
                  initialValues={{ ...item, ...ADMINISTRATION_BOOKKEEPING_CATEGORIES.fields }}
                  onSubmit={(values, formikHelpers) => {
                    return updateAdministrationItem('Category', administrationData.machineId, values, formikHelpers.setSubmitting, page - 1, administrationData.searchText);
                  }}>
                  {({ isSubmitting, dirty, setFieldValue, values }) => {
                    return (
                      
                      <Form className={classNames(
                        classes.formContent,
                        accordion.includes(index) && classes.expanded)}>
                        <AdministrationItemsFormFields
                          values={values}
                          rows={rows}
                          classes={classes}
                          setFieldValue={setFieldValue}
                          vatOptions={vatOptions}
                          isUpdate={true}
                          itemIndex={index}
                          accordion={accordion}
                          setAccordion={setAccordion}
                        />
                        
                        <CommonButton
                          className={classes.saveButton}
                          variant="outlined"
                          color="secondary"
                          type="submit"
                          label={
                            <FormattedMessage
                              id="button.save"
                              defaultMessage="Save"
                            />
                          }
                          disabled={!dirty || isSubmitting}
                        />
                      </Form>
                    );
                  }}
                </Formik>
              </Paper>;
            })}
    
            {getPagination()}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

AdministrationBookkeepingPopup.propTypes = {
  machineId: PropTypes.string,
  selectedMachineLabel: PropTypes.string,
  vatOptions: PropTypes.array,
  activePopup: PropTypes.bool,
  setPopupActive: PropTypes.func,
  administrationData: PropTypes.object,
  fetchAdministrationData: PropTypes.func,
  updateAdministrationItem: PropTypes.func,
};

const decorate = compose(
  connect(store => ({
      administrationData: store.administrationData.administrationData,
    }),
    dispatch => bindActionCreators({ updateAdministrationItem, fetchAdministrationData }, dispatch)),
);

export default decorate(AdministrationBookkeepingPopup);